import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Testimonials from '../components/Testimonials';
import ContactForm from '../components/ContactForm';
import ContactArrivalModal from '../components/ContactArrivalModal';

const Home = () => {
  useEffect(() => {
    // Animation au scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right, .scale-in');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <>
      <ContactArrivalModal />
      {/* Hero Section */}
      <section 
        className="modern-hero home-hero-with-form"
        style={{
          backgroundImage: 'url(/wp-content/uploads/2025/02/comment_tailler_haie-scaled-1.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative'
        }}
      >
        <div className="home-hero-layered">
          <div className="home-hero-overlay" aria-hidden="true" />
          <div className="modern-hero-content home-hero-layout home-hero-foreground fade-in">
            <div className="home-hero-copy">
              <div className="hero-badge">Rendre vos jardins meilleurs</div>
              <h1 className="hero-title" style={{ margin: 0, marginBottom: '1.5rem' }}>Crédit d'impôt -50% !</h1>
              <p className="hero-description">
                Parce que chaque jardin est unique, nous vous accompagnons avec passion pour créer, embellir et entretenir votre espace vert. Profitez d'un extérieur à votre image, toute l'année !
              </p>
              <Link to="/services" className="modern-button">
                <span>Demande de devis</span>
                <svg width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
                  <path d="M256 8c137 0 248 111 248 248S393 504 256 504 8 393 8 256 119 8 256 8zm-28.9 143.6l75.5 72.4H120c-13.3 0-24 10.7-24 24v16c0 13.3 10.7 24 24 24h182.6l-75.5 72.4c-9.7 9.3-9.9 24.8-.4 34.3l11 10.9c9.4 9.4 24.6 9.4 33.9 0L404.3 273c9.4-9.4 9.4-24.6 0-33.9L271.6 106.3c-9.4-9.4-24.6-9.4-33.9 0l-11 10.9c-9.5 9.6-9.3 25.1.4 34.4z"></path>
                </svg>
              </Link>
            </div>
            <div className="home-hero-form">
              <p className="home-hero-form-intro">Contact rapide — nous vous répondons sous 24h</p>
              <ContactForm type="basic" embedded idPrefix="hero-" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Layout avec ligne pointillée */}
      <section className="modern-section" style={{background: 'var(--color-beige-light)'}}>
        <div className="section-container">
          <div className="services-layout-grid">
            {/* Colonne gauche - Texte */}
            <div className="slide-in-left">
              <div className="section-badge" style={{marginBottom: '2rem'}}>Pour un espace plus vert</div>
              <h2 className="section-title" style={{textAlign: 'left', marginBottom: '2rem', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)'}}>Vos choix pour un jardin de rêve</h2>
              <p className="section-description" style={{textAlign: 'left', marginBottom: '2.5rem', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.8'}}>
                Aménagez et entretenez un jardin à votre image grâce à nos conseils, astuces et solutions adaptées. Profitez d'un espace extérieur harmonieux et verdoyant toute l'année !
              </p>
              <Link to="/contact" className="modern-button button-left-align">
                <span>Demande de devis</span>
                <svg width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
                  <path d="M256 8c137 0 248 111 248 248S393 504 256 504 8 393 8 256 119 8 256 8zm-28.9 143.6l75.5 72.4H120c-13.3 0-24 10.7-24 24v16c0 13.3 10.7 24 24 24h182.6l-75.5 72.4c-9.7 9.3-9.9 24.8-.4 34.3l11 10.9c9.4 9.4 24.6 9.4 33.9 0L404.3 273c9.4-9.4 9.4-24.6 0-33.9L271.6 106.3c-9.4-9.4-24.6-9.4-33.9 0l-11 10.9c-9.5 9.6-9.3 25.1.4 34.4z"></path>
                </svg>
              </Link>
            </div>

            {/* Ligne pointillée verticale */}
            <div className="dotted-line"></div>

            {/* Colonne droite - Particulier et Entreprise */}
            <div className="slide-in-right services-cards">
              {/* Particulier */}
              <div style={{textAlign: 'center', flex: 1}}>
                <div className="card-icon scale-in" style={{
                  width: '120px', 
                  height: '120px', 
                  background: 'var(--color-primary)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 2rem', 
                  color: 'var(--color-white)',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 31 31" fill="currentColor">
                    <path d="M30.5728 13.3682L16.3638 2.96777C16.2271 2.85059 16.0708 2.79199 15.895 2.79199C15.7192 2.79199 15.563 2.85059 15.4263 2.96777L1.21729 13.3682C1.0415 13.4854 0.938965 13.6514 0.909668 13.8662C0.880371 14.0811 0.924316 14.2764 1.0415 14.4521C1.17822 14.6279 1.34912 14.7305 1.5542 14.7598C1.75928 14.7891 1.94971 14.7451 2.12549 14.6279L15.895 4.5498L29.6646 14.6279C29.7231 14.667 29.7915 14.7012 29.8696 14.7305C29.9478 14.7598 30.0259 14.7744 30.104 14.7744C30.2407 14.7744 30.3628 14.7451 30.4702 14.6865C30.5776 14.6279 30.6704 14.5498 30.7485 14.4521C30.8657 14.2764 30.9097 14.0811 30.8804 13.8662C30.8511 13.6514 30.7485 13.4854 30.5728 13.3682ZM26.7935 14.8037C26.5786 14.8037 26.3979 14.877 26.2515 15.0234C26.105 15.1699 26.0317 15.3506 26.0317 15.5654V27.9873H19.7915V21.2197C19.7915 20.1455 19.4106 19.2275 18.6489 18.4658C17.8872 17.7041 16.9692 17.3232 15.895 17.3232C14.8208 17.3232 13.9028 17.7041 13.1411 18.4658C12.3794 19.2275 11.9985 20.1455 11.9985 21.2197V27.9873H5.7583V15.5654C5.7583 15.3506 5.68506 15.1699 5.53857 15.0234C5.39209 14.877 5.21143 14.8037 4.99658 14.8037C4.76221 14.8037 4.57178 14.877 4.42529 15.0234C4.27881 15.1699 4.20557 15.3506 4.20557 15.5654V28.7783C4.20557 28.9932 4.27881 29.1738 4.42529 29.3203C4.57178 29.4668 4.76221 29.54 4.99658 29.54H12.7896C12.9849 29.54 13.1558 29.4717 13.3022 29.335C13.4487 29.1982 13.5317 29.0322 13.5513 28.8369C13.5513 28.8174 13.5513 28.8027 13.5513 28.793C13.5513 28.7832 13.5513 28.7783 13.5513 28.7783V21.2197C13.5513 20.5752 13.7808 20.0234 14.2397 19.5645C14.6987 19.1055 15.2505 18.876 15.895 18.876C16.5396 18.876 17.0913 19.1055 17.5503 19.5645C18.0093 20.0234 18.2388 20.5752 18.2388 21.2197V28.7783C18.2388 28.7783 18.2388 28.7832 18.2388 28.793C18.2388 28.8027 18.2388 28.8174 18.2388 28.8369C18.2583 29.0322 18.3413 29.1982 18.4878 29.335C18.6343 29.4717 18.8052 29.54 19.0005 29.54H26.7935C27.0278 29.54 27.2183 29.4668 27.3647 29.3203C27.5112 29.1738 27.5845 28.9932 27.5845 28.7783V15.5654C27.5845 15.3506 27.5112 15.1699 27.3647 15.0234C27.2183 14.877 27.0278 14.8037 26.7935 14.8037Z" fill="currentColor"></path>
                  </svg>
                </div>
                <h3 style={{fontSize: 'clamp(1.5rem, 3vw, 1.75rem)', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '2rem', fontFamily: 'var(--font-heading)'}}>Particulier</h3>
                <Link to="/contact?service=particulier" className="modern-button" style={{padding: '0.875rem 2rem', fontSize: '1rem'}}>
                  <span>Devis</span>
                </Link>
              </div>

              {/* Entreprise */}
              <div style={{textAlign: 'center', flex: 1}}>
                <div className="card-icon scale-in" style={{
                  width: '120px', 
                  height: '120px', 
                  background: 'var(--color-white)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 2rem', 
                  padding: '1rem',
                  border: '3px solid var(--color-primary)',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <img src="/wp-content/uploads/2025/02/icon-leaf-green.svg" alt="CallGarden Logo" loading="lazy" style={{width: '100%', height: '100%', objectFit: 'contain'}} />
                </div>
                <h3 style={{fontSize: 'clamp(1.5rem, 3vw, 1.75rem)', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '2rem', fontFamily: 'var(--font-heading)'}}>Entreprise</h3>
                <Link to="/contact?service=entreprises" className="modern-button" style={{padding: '0.875rem 2rem', fontSize: '1rem'}}>
                  <span>Devis</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="modern-section">
        <div className="section-container">
          <div className="section-header fade-in">
            <div className="section-badge">Bienvenue chez Callgarden</div>
            <h2 className="section-title">Entretien expert pour vos espaces verts</h2>
          </div>
          
          <div className="welcome-layout-grid">
            {/* Texte à gauche */}
            <div className="slide-in-left" style={{textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <p className="page-text" style={{marginBottom: '2rem', fontSize: '1.125rem', lineHeight: '1.8', maxWidth: '100%'}}>
                Callgarden est une entreprise spécialisée dans plusieurs domaines d'entretien extérieur. Nous offrons des services de taille de haies, d'élagage, de tonte, de nettoyage de terrasses ainsi que d'entretien de pelouse incluant engrais, arrosage, désherbage, conseils…
              </p>
              <p className="page-text" style={{marginBottom: '2.5rem', fontSize: '1.125rem', lineHeight: '1.8', maxWidth: '100%'}}>
                Notre équipe garantit des résultats de qualité pour un jardin sain et esthétique.
              </p>

              {/* Deux icônes en dessous du texte */}
              <div className="responsive-flex" style={{marginBottom: '2.5rem', justifyContent: 'center', flexWrap: 'wrap', gap: '1.5rem', width: '100%'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '1.25rem', justifyContent: 'center'}}>
                  <div className="card-icon scale-in" style={{
                    width: '70px', 
                    height: '70px', 
                    background: 'var(--color-primary)', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: 'var(--color-white)', 
                    fontSize: '1.75rem', 
                    flexShrink: 0,
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    <img src="/imgsection/div3icon1.svg" alt="Équipe" style={{width: '40px', height: '40px'}} />
                  </div>
                  <h3 style={{fontSize: '1.25rem', fontWeight: '600', color: 'var(--color-primary)', fontFamily: 'var(--font-heading)', textAlign: 'center'}}>Une équipe organisée</h3>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '1.25rem', justifyContent: 'center'}}>
                  <div className="card-icon scale-in" style={{
                    width: '70px', 
                    height: '70px', 
                    background: 'var(--color-primary)', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: 'var(--color-white)', 
                    fontSize: '1.75rem', 
                    flexShrink: 0,
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    <img src="/imgsection/div3icon2.svg" alt="Espace vert" style={{width: '40px', height: '40px'}} />
                  </div>
                  <h3 style={{fontSize: '1.25rem', fontWeight: '600', color: 'var(--color-primary)', fontFamily: 'var(--font-heading)', textAlign: 'center'}}>Espace vert de qualité</h3>
                </div>
              </div>

              <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                <Link to="/contact" className="modern-button">
                  <span>Demande de devis</span>
                  <svg width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
                    <path d="M256 8c137 0 248 111 248 248S393 504 256 504 8 393 8 256 119 8 256 8zm-28.9 143.6l75.5 72.4H120c-13.3 0-24 10.7-24 24v16c0 13.3 10.7 24 24 24h182.6l-75.5 72.4c-9.7 9.3-9.9 24.8-.4 34.3l11 10.9c9.4 9.4 24.6 9.4 33.9 0L404.3 273c9.4-9.4 9.4-24.6 0-33.9L271.6 106.3c-9.4-9.4-24.6-9.4-33.9 0l-11 10.9c-9.5 9.6-9.3 25.1.4 34.4z"></path>
                  </svg>
                </Link>
              </div>
            </div>

            {/* Images polaroid à droite - superposées */}
            <div className="slide-in-right polaroid-container" style={{position: 'relative', width: '100%', maxWidth: '400px', marginLeft: 'auto', marginRight: '0'}}>
              <div className="polaroid-image polaroid-1" style={{
                width: '260px', 
                maxWidth: '100%',
                position: 'absolute',
                top: '10px',
                left: '-20px',
                zIndex: 1,
                transform: 'rotate(-5deg)',
                transition: 'transform 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(-3deg) scale(1.05)';
                e.currentTarget.style.zIndex = '3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(-5deg)';
                e.currentTarget.style.zIndex = '1';
              }}>
                <img src="/imgsection/div3img1.webp" alt="Jardinier" loading="lazy" />
              </div>
              <div className="polaroid-image polaroid-2" style={{
                width: '260px',
                maxWidth: '100%',
                position: 'absolute',
                top: '70px',
                left: '140px',
                zIndex: 2,
                transform: 'rotate(6deg)', 
                transition: 'transform 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(4deg) scale(1.05)';
                e.currentTarget.style.zIndex = '3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(6deg)';
                e.currentTarget.style.zIndex = '2';
              }}>
                <img src="/imgsection/div3img2.webp" alt="Entretien jardin" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pelouse haute gamme Section */}
      <section className="modern-section" style={{background: 'var(--color-primary-dark)', color: 'var(--color-white)', position: 'relative', padding: '3rem 0'}}>
        <div style={{
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundImage: 'url(/imgsection/div4img1.webp)', 
          backgroundSize: 'cover', 
          backgroundPosition: 'center', 
          zIndex: 0
        }}></div>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(10, 50, 20, 0.5)',
          zIndex: 0
        }}></div>
        <div className="section-container" style={{position: 'relative', zIndex: 1, padding: '0 1.8rem'}}>
          <div className="section-header fade-in" style={{textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <h2 className="section-title" style={{color: 'var(--color-white)', fontSize: 'clamp(1.75rem, 5vw, 3rem)', textAlign: 'center'}}>Un entretien de pelouse haute gamme</h2>
            <p className="section-description" style={{color: 'rgba(255,255,255,0.95)', maxWidth: '900px', fontSize: 'clamp(1rem, 2vw, 1.125rem)', textAlign: 'center', margin: '0 auto'}}>
              Un entretien de pelouse haut de gamme comprend une tonte régulière, un arrosage adapté, une fertilisation et un désherbage, pour garantir une pelouse saine et d'une couleur parfaite.
            </p>
          </div>

          <div className="cards-grid" style={{marginTop: '4rem'}}>
            <div className="modern-card green-border scale-in" style={{
              background: 'var(--color-white)', 
              borderColor: 'var(--color-primary)', 
              borderWidth: '3px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}>
              <div style={{textAlign: 'center', marginBottom: '2rem'}}>
                <div style={{
                  width: '160px', 
                  height: '160px', 
                  background: 'var(--color-accent-light)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto', 
                  color: 'var(--color-primary)', 
                  fontSize: '1.75rem', 
                  fontWeight: 'bold', 
                  lineHeight: '1.3', 
                  padding: '1.5rem', 
                  position: 'relative'
                }}>
                  <div style={{fontSize: '2.5rem', fontWeight: '700'}}>50%</div>
                  <div style={{fontSize: '1.125rem'}}>réduction</div>
                  <div style={{fontSize: '1.125rem'}}>d'impôt</div>
                </div>
              </div>
              <h3 className="card-title" style={{color: 'var(--color-primary)', fontSize: '1.5rem', marginBottom: '1rem'}}>Crédit d'impôt jardin</h3>
              <p className="card-description" style={{color: 'var(--color-gray)', fontSize: '1rem', marginBottom: '1.5rem'}}>
                Crédit d'impôt jardin : comment bénéficier de cet avantage fiscal...
              </p>
              <div style={{textAlign: 'center'}}>
                <Link to="/services/credit-dimpot-jardin" className="modern-button" style={{padding: '0.875rem 2rem', fontSize: '1rem'}}>
                  <span>En savoir plus</span>
                </Link>
              </div>
            </div>

            <div className="modern-card green-border scale-in" style={{
              background: 'var(--color-white)', 
              borderColor: 'var(--color-primary)', 
              borderWidth: '3px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}>
              <div className="card-image-container" style={{width: '100%', aspectRatio: '16/9', background: 'var(--color-light)', borderRadius: '12px', marginBottom: '1.5rem', overflow: 'hidden', position: 'relative'}}>
                <img src="/wp-content/uploads/2025/02/jardins-d-entreprise.jpg" alt="Service entreprises" loading="lazy" style={{width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease'}} 
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'} />
              </div>
              <h3 className="card-title" style={{color: 'var(--color-primary)', fontSize: '1.5rem', marginBottom: '1rem'}}>Service entreprises</h3>
              <p className="card-description" style={{color: 'var(--color-gray)', fontSize: '1rem', marginBottom: '1.5rem'}}>
                La société Callgarden est compétente pour l'entretien de vos espaces...
              </p>
              <div style={{textAlign: 'center'}}>
                <Link to="/services/service-entreprises" className="modern-button" style={{padding: '0.875rem 2rem', fontSize: '1rem'}}>
                  <span>En savoir plus</span>
                </Link>
              </div>
            </div>

            <div className="modern-card green-border scale-in" style={{
              background: 'var(--color-white)', 
              borderColor: 'var(--color-primary)', 
              borderWidth: '3px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}>
              <div className="card-image-container" style={{width: '100%', aspectRatio: '16/9', background: 'var(--color-light)', borderRadius: '12px', marginBottom: '1.5rem', overflow: 'hidden', position: 'relative'}}>
                <img src="/wp-content/uploads/2025/02/lawn-aeration-using-powerful-gasoline-powered-aera-2023-11-27-05-02-15-utc-scaled.jpg" alt="Service Particulier" loading="lazy" style={{width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease'}}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'} />
              </div>
              <h3 className="card-title" style={{color: 'var(--color-primary)', fontSize: '1.5rem', marginBottom: '1rem'}}>Service Particulier</h3>
              <p className="card-description" style={{color: 'var(--color-gray)', fontSize: '1rem', marginBottom: '1.5rem'}}>
                La société Callgarden est spécialisée dans l'entretien de jardin sur...
              </p>
              <div style={{textAlign: 'center'}}>
                <Link to="/services/service-particulier" className="modern-button" style={{padding: '0.875rem 2rem', fontSize: '1rem'}}>
                  <span>En savoir plus</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comment nous travaillons Section */}
      <section className="modern-section" style={{background: 'var(--color-beige-light)'}}>
        <div className="section-container">
          <div className="process-layout-grid">
            <div className="slide-in-left">
              <div className="section-badge" style={{marginBottom: '2rem'}}>Comment nous travaillons</div>
              <h2 className="section-title" style={{textAlign: 'left', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)'}}>Notre processus de travail</h2>
            </div>
            <div className="slide-in-right">
              <p className="section-description" style={{textAlign: 'left', fontSize: '1.125rem', lineHeight: '1.8'}}>
                Nous commençons par une analyse de votre jardin pour comprendre vos besoins. De surcroit, nous vous proposons un plan d'entretien et un devis adapté. Une fois validé, nous réalisons les travaux avec soin. In fine, nous assurons un suivi régulier et vous offrons des conseils pour entretenir votre jardin.
              </p>
            </div>
          </div>

          <div className="process-steps-container">
            {[
              { icon: '/imgsection/1 Nous contactez.svg', title: 'Nous contactez', desc: 'Un espace vert réalisé selon vos attentes' },
              { icon: '/imgsection/2 devis.svg', title: 'Convenir à un devis', desc: 'Un devis adapté à vos besoins' },
              { icon: '/imgsection/3 JourJ.svg', title: 'Jour-J', desc: 'Nos experts seront réunis pour effectuer le jardin de vos rêves' },
              { icon: '/imgsection/4 espace Vert.svg', title: 'Entretien de l\'espace vert', desc: 'Nous entretenons vos espaces verts suivant leurs besoins' }
            ].map((step, index) => (
              <React.Fragment key={index}>
                <div className="process-step scale-in">
                  <div className="card-icon process-step-icon">
                    <img src={step.icon} alt={step.title} style={{width: '50px', height: '50px', objectFit: 'contain'}} />
                  </div>
                  <h3 className="card-title">{step.title}</h3>
                  <p className="card-description">
                    {step.desc}
                  </p>
                </div>
                {index < 3 && (
                  <div className="process-arrow">
                    <svg width="50" height="50" viewBox="0 0 512 512" fill="var(--color-primary)" style={{opacity: 0.6}}>
                      <path d="M256 8c137 0 248 111 248 248S393 504 256 504 8 393 8 256 119 8 256 8zm-28.9 143.6l75.5 72.4H120c-13.3 0-24 10.7-24 24v16c0 13.3 10.7 24 24 24h182.6l-75.5 72.4c-9.7 9.3-9.9 24.8-.4 34.3l11 10.9c9.4 9.4 24.6 9.4 33.9 0L404.3 273c9.4-9.4 9.4-24.6 0-33.9L271.6 106.3c-9.4-9.4-24.6-9.4-33.9 0l-11 10.9c-9.5 9.6-9.3 25.1.4 34.4z"></path>
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignages Section */}
      <Testimonials />

      {/* Herbe si fraîche Section */}
      <section className="modern-section" style={{background: 'var(--color-primary-dark)', color: 'var(--color-white)', position: 'relative', minHeight: '400px', padding: '3rem 0'}}>
        <div style={{
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundImage: 'url(/imgsection/div4img1.webp)', 
          backgroundSize: 'cover', 
          backgroundPosition: 'center', 
          zIndex: 0
        }}></div>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)',
          zIndex: 0
        }}></div>
        <div className="section-container" style={{
          position: 'relative', 
          zIndex: 1,
          padding: '0 1.8rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px'
        }}>
          <div className="slide-in-left" style={{textAlign: 'center', width: '100%', maxWidth: '900px', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <div className="section-badge" style={{background: 'var(--color-accent)', color: 'var(--color-primary)', marginBottom: '1rem'}}>Herbe si fraîche</div>
            <h2 className="section-title" style={{color: 'var(--color-white)', textAlign: 'center', marginBottom: '1rem', fontSize: 'clamp(1.75rem, 5vw, 3rem)'}}>Entretien de pelouse digne de la royauté</h2>
            <p className="section-description" style={{color: 'rgba(255,255,255,0.95)', textAlign: 'center', maxWidth: '650px', margin: '0 auto 1.5rem', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.8'}}>
              Offrez à votre pelouse le traitement royal qu'elle mérite. Un entretien soigné, professionnel et élégant pour un gazon verdoyant, digne des plus grands palais.
            </p>
            <div className="responsive-flex" style={{flexWrap: 'wrap', justifyContent: 'center', gap: '1rem', width: '100%'}}>
              <Link to="/services/la-tonte" className="modern-button" style={{background: 'var(--color-accent)', color: 'var(--color-primary)'}}>
                <span>En savoir plus</span>
                <svg width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
                  <path d="M256 8c137 0 248 111 248 248S393 504 256 504 8 393 8 256 119 8 256 8zm-28.9 143.6l75.5 72.4H120c-13.3 0-24 10.7-24 24v16c0 13.3 10.7 24 24 24h182.6l-75.5 72.4c-9.7 9.3-9.9 24.8-.4 34.3l11 10.9c9.4 9.4 24.6 9.4 33.9 0L404.3 273c9.4-9.4 9.4-24.6 0-33.9L271.6 106.3c-9.4-9.4-24.6-9.4-33.9 0l-11 10.9c-9.5 9.6-9.3 25.1.4 34.4z"></path>
                </svg>
              </Link>
              <Link to="/contact" className="modern-button secondary" style={{background: 'var(--color-beige-light)', color: 'var(--color-primary)'}}>
                <span>Contactez-nous</span>
                <svg width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
                  <path d="M256 8c137 0 248 111 248 248S393 504 256 504 8 393 8 256 119 8 256 8zm-28.9 143.6l75.5 72.4H120c-13.3 0-24 10.7-24 24v16c0 13.3 10.7 24 24 24h182.6l-75.5 72.4c-9.7 9.3-9.9 24.8-.4 34.3l11 10.9c9.4 9.4 24.6 9.4 33.9 0L404.3 273c9.4-9.4 9.4-24.6 0-33.9L271.6 106.3c-9.4-9.4-24.6-9.4-33.9 0l-11 10.9c-9.5 9.6-9.3 25.1.4 34.4z"></path>
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
